'use client';

import {
  collection,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/clientApp';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';

// Collection name for branding in Firestore
const BRANDING_COLLECTION = 'branding';
const BRANDING_DOC_ID = 'site-branding';

/**
 * Interface for branding configuration
 */
export interface BrandingConfig {
  id?: string;

  // Basic branding - Original fields
  logoUrl: string;
  smallLogoUrl?: string; // For smaller displays
  faviconUrl: string;
  faviconSizes?: {
    [size: string]: string; // e.g. "72x72": "/icons/custom-icon-72x72.webp"
  };

  // QR Code
  qrCodeUrl?: string; // URL for the QR code image to use on the contact page

  // Contact Information
  contactInfo?: {
    address?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    phone?: string;
    secondaryPhone?: string;
    email?: string;
    checkInOut?: string;
  };

  // Visual Identity
  visualIdentity?: {
    primaryColor?: string; // HEX color code for primary brand color
    secondaryColor?: string; // HEX color code for secondary brand color
    accentColor?: string; // HEX color code for accent highlights
    fontPrimary?: string; // Primary font family
    fontSecondary?: string; // Secondary font family
  };

  // Social Media Links
  socialMedia?: {
    facebook?: string;
    instagram?: string;
    youtube?: string;
    twitter?: string;
    linkedin?: string;
    other?: { [platform: string]: string };
  };

  // Footer Configuration
  footerConfig?: {
    copyright?: string;
    privacyPolicyUrl?: string;
    termsOfServiceUrl?: string;
    quickLinks?: Array<{ label: string; url: string }>;
    disclaimer?: string;
  };

  // Landing Page Content
  landingPageContent?: {
    // CTA Buttons
    ctaButtons?: {
      primaryLabel?: string;
      primaryUrl?: string;
      secondaryLabel?: string;
      secondaryUrl?: string;
    };
    // First feature section - Property Overview
    propertyOverview?: {
      title?: string;
      description1?: string;
      description2?: string;
      linkText?: string;
      linkUrl?: string;
    };
    // Second feature section - Location Info
    locationInfo?: {
      title?: string;
      description1?: string;
      description2?: string;
      linkText?: string;
      linkUrl?: string;
    };
  };

  // SEO & Metadata
  seoMetadata?: {
    siteDescription?: string;
    keywords?: string[];
    googleAnalyticsId?: string;
    googleTagManagerId?: string;
    customMetaTags?: Array<{ name: string; content: string }>;
    // Domain configuration for SEO improvements
    primaryDomain?: string; // e.g., "tannenboden.com"
    currentDomain?: string; // e.g., "tannenboden.com" (temporary)
    useWwwPrefix?: boolean; // Whether to use www prefix in canonical URLs
    alternateLanguageDomains?: Record<string, string>; // Maps language codes to full URLs
  };

  // Menu Configuration
  menuConfiguration?: {
    // Store visibility for submenu items
    submenuItemsVisibility?: {
      [menuKey: string]: {
        [submenuKey: string]: boolean; // e.g., "property": { "amenities": true, "gallery": true }
      };
    };
  };

  // System fields
  updatedAt?: Timestamp;
  createdAt?: Timestamp;
}

/**
 * Default branding configuration with standard icon paths
 */
export const DEFAULT_BRANDING: BrandingConfig = {
  // Basic branding
  logoUrl: '/icons/logo.webp',
  smallLogoUrl: '/icons/icon-72x72.webp',
  faviconUrl: '/icons/icon-72x72.webp',
  faviconSizes: {
    '72x72': '/icons/icon-72x72.webp',
    '96x96': '/icons/icon-96x96.webp',
    '128x128': '/icons/icon-128x128.webp',
    '384x384': '/icons/icon-384x384.webp',
    '512x512': '/icons/icon-512x512.webp',
  },
  qrCodeUrl: '/images/qrcode.webp',

  // Contact Information
  contactInfo: {
    address: 'Sattelboden 1',
    city: 'Engelberg',
    postalCode: '6390',
    country: 'Switzerland',
    phone: '+41 78 746 04 47',
    email: 'pavel.maxim@tannenboden.com',
    checkInOut: 'Check-in: 15:00 - 19:00, Check-out: bis 10:00',
  },

  // Visual Identity
  visualIdentity: {
    primaryColor: '#1a3d4d', // alpine blue
    secondaryColor: '#4a7c59', // pine green
    accentColor: '#c8a158', // warm gold
  },

  // Social Media Links
  socialMedia: {
    facebook: 'https://www.facebook.com/tannenbodenChalet',
    instagram: 'https://www.instagram.com/tannenboden_chalet',
    // Add other social media as they become available
  },

  // Landing Page Content
  landingPageContent: {
    // CTA Buttons
    ctaButtons: {
      primaryLabel: 'Verfügbarkeit prüfen',
      primaryUrl: '/de/engelberg-ferienwohnung/verfugbarkeit',
      secondaryLabel: 'Virtuelle Tour',
      secondaryUrl: 'https://my.matterport.com/show/?m=FpvN3DrCoa7',
    },
    // First feature section - Property Overview
    propertyOverview: {
      title: 'Ferienwohnung Engelberg mit Bergblick',
      description1:
        'Erleben Sie einen perfekten Aufenthalt in der Tannenboden Ferienwohnung in Engelberg. Unsere gemütliche Bergunterkunft bietet atemberaubenden Blick auf Titlis und Hahnen, komfortable Ausstattung und direkten Zugang zu erstklassigen Ski- und Wanderwegen.',
      description2:
        'Mit separaten Wohneinheiten im Ober- und Untergeschoss, die zusammen oder einzeln gemietet werden können, ist Tannenboden ideal für Familienurlaub und Gruppenreisen in den Schweizer Alpen.',
      linkText: 'Ferienwohnung entdecken',
      linkUrl: '/de/engelberg-ferienwohnung/chalet',
    },
    // Second feature section - Location Info
    locationInfo: {
      title: 'Engelberg Erlebnis',
      description1:
        'Engelberg bietet ganzjährige Aktivitäten für Naturliebhaber und Abenteurer. Im Winter genießen Sie über 80 km Skipisten, von anfängerfreundlichen Abfahrten bis zu anspruchsvollem Gelände. Der Sommer bringt außergewöhnliche Wander-, Mountainbike- und Paragliding-Möglichkeiten.',
      description2:
        'Nur wenige Gehminuten von der Tannenboden Ferienwohnung entfernt finden Sie charmante Dorfläden, Gourmet-Restaurants und das historische Engelberger Kloster. Die Titlis-Seilbahnstation ist leicht zu erreichen und bietet schnellen Zugang zum über 3.000 Meter hohen Gipfel mit seinem ganzjährigen Gletscher.',
      linkText: 'Engelberg entdecken',
      linkUrl: '/de/engelberg-ferienwohnung/sehenswuerdigkeiten',
    },
  },

  // Footer Configuration
  footerConfig: {
    copyright:
      '© 2025 Tannenboden Ferienwohnung Engelberg. Alle Rechte vorbehalten.',
    quickLinks: [
      {
        label: 'Verfügbarkeit',
        url: '/de/engelberg-ferienwohnung/verfugbarkeit',
      },
      { label: 'Galerie', url: '/de/engelberg-ferienwohnung/galerie' },
      { label: 'Kontakt', url: '/de/engelberg-ferienwohnung/kontakt' },
      { label: 'Standort', url: '/de/engelberg-ferienwohnung/karte' },
    ],
  },

  // SEO & Metadata
  seoMetadata: {
    siteDescription:
      'Tannenboden Ferienwohnung Engelberg - Gemütliche Bergunterkunft mit atemberaubendem Titlis-Blick. Perfekt für Ski- und Wanderurlaub in den Schweizer Alpen.',
    keywords: [
      'ferienwohnung engelberg',
      'unterkunft engelberg',
      'ferienhaus engelberg',
      'engelberg titlis unterkunft',
      'bergunterkunft engelberg',
      'gemütliche ferienwohnung',
      'ski apartment engelberg',
      'titlis view apartment',
      'engelberg accommodation',
      'appartement de vacances engelberg',
      'hébergement engelberg',
      'apartamento engelberg',
    ],
    googleAnalyticsId: 'G-P4L4WQ82RL',
    primaryDomain: 'tannenboden.com',
    currentDomain: 'tannenboden.com',
    useWwwPrefix: false,
    alternateLanguageDomains: {
      de: 'https://tannenboden.com/de/engelberg-ferienwohnung',
      en: 'https://tannenboden.com/engelberg-accommodation',
      fr: 'https://tannenboden.com/fr/hebergement-engelberg',
      es: 'https://tannenboden.com/es/alojamiento-engelberg',
    },
  },

  // Menu Configuration - By default, all submenu items are visible
  menuConfiguration: {
    submenuItemsVisibility: {
      property: {
        ferienwohnung: true,
        ausstattung: true,
        galerie: true,
        virtuelleTour: true,
      },
      location: {
        engelberg: true,
        anfahrt: true,
        sehenswuerdigkeiten: true,
        restaurants: true,
      },
      activities: {
        winter: true,
        sommer: true,
        familienAktivitaeten: true,
        abenteuersport: true,
      },
      bookingInfo: {
        verfugbarkeit: true,
        preise: true,
        buchungsbedingungen: true,
        faq: true,
      },
      contact: {
        kontakt: true,
        anfrage: true,
        bewertungen: true,
      },
    },
  },
};

/**
 * Removes undefined values from an object to avoid Firestore errors
 */
function removeUndefinedValues(obj: any): any {
  const newObj: any = {};

  Object.keys(obj).forEach((key) => {
    if (obj[key] !== undefined) {
      newObj[key] = obj[key];
    }
  });

  return newObj;
}

/**
 * Get the current branding configuration
 * @returns Promise with the branding configuration or default values if not set
 */
export async function getBrandingConfig(): Promise<BrandingConfig> {
  try {
    const docRef = doc(db, BRANDING_COLLECTION, BRANDING_DOC_ID);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as BrandingConfig;
    } else {
      return DEFAULT_BRANDING;
    }
  } catch (error) {
    console.error('Error getting branding config:', error);
    return DEFAULT_BRANDING;
  }
}

/**
 * Save or update the branding configuration
 * @param config The branding configuration to save
 * @returns Promise indicating success
 */
export async function saveBrandingConfig(
  config: Partial<BrandingConfig>
): Promise<void> {
  try {
    // Remove any undefined values to avoid Firestore errors
    const cleanConfig = removeUndefinedValues(config);

    // Add timestamps
    const dataToSave = {
      ...cleanConfig,
      updatedAt: serverTimestamp(),
    };

    const docRef = doc(db, BRANDING_COLLECTION, BRANDING_DOC_ID);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      // Update existing configuration
      await updateDoc(docRef, dataToSave);
    } else {
      // Create new configuration with creation timestamp
      await setDoc(docRef, {
        ...dataToSave,
        createdAt: serverTimestamp(),
      });
    }

    // Add a notification mechanism to trigger navigation refresh
    localStorage.setItem('branding_updated', Date.now().toString());
  } catch (error) {
    console.error('Error saving branding config:', error);
    throw error;
  }
}

/**
 * Upload an image and return the storage URL
 * @param file The file to upload
 * @param path Storage path (e.g., 'logo', 'favicon')
 * @returns Promise with the download URL
 */
export async function uploadBrandingImage(
  file: File,
  path: string
): Promise<string> {
  try {
    const storage = getStorage();
    const timestamp = new Date().getTime();
    const filename = `${path}-${timestamp}-${file.name.replace(/\s+/g, '-')}`;
    const storageRef = ref(storage, `branding/${filename}`);

    // Upload the file
    const snapshot = await uploadBytes(storageRef, file);

    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error('Error uploading branding image:', error);
    throw error;
  }
}

/**
 * Convert a PNG image to JPEG format with specified quality
 * @param imageFile The PNG file to convert
 * @param quality JPEG quality (0-1)
 * @returns Promise with the converted Blob
 */
export async function convertPngToJpeg(
  imageFile: File,
  quality: number = 0.95
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        // Draw the image on the canvas
        ctx.drawImage(img, 0, 0);

        // Convert to JPEG
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to convert image to JPEG'));
            }
          },
          'image/jpeg',
          quality
        );
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(imageFile);
  });
}

/**
 * Generate multiple icon sizes from a single image
 * @param imageFile The original image file
 * @param sizes Array of sizes to generate (e.g., [72, 96, 128])
 * @returns Promise with an object mapping sizes to Blobs
 */
export async function generateIconSizes(
  imageFile: File,
  sizes: number[] = [72, 96, 128, 384, 512]
): Promise<Record<string, Blob>> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const result: Record<string, Blob> = {};

    reader.onload = (e) => {
      const img = new Image();

      img.onload = async () => {
        try {
          for (const size of sizes) {
            const sizeKey = `${size}x${size}`;
            const blob = await resizeImage(img, size, size);
            result[sizeKey] = blob;
          }
          resolve(result);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(imageFile);
  });
}

/**
 * Resize an image to specified dimensions
 * @param image The image element to resize
 * @param width Target width
 * @param height Target height
 * @returns Promise with the resized image as a Blob
 */
function resizeImage(
  image: HTMLImageElement,
  width: number,
  height: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('Failed to get canvas context'));
      return;
    }

    // Draw the image on the canvas, resizing it
    ctx.drawImage(image, 0, 0, width, height);

    // Convert to JPEG
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to resize image'));
        }
      },
      'image/jpeg',
      0.95
    );
  });
}

/**
 * Upload multiple icon sizes to Firebase Storage
 * @param iconBlobs Object mapping sizes to Blobs
 * @param basePath Base path for storage (e.g., 'favicon')
 * @returns Promise with an object mapping sizes to download URLs
 */
export async function uploadIconSizes(
  iconBlobs: Record<string, Blob>,
  basePath: string
): Promise<Record<string, string>> {
  const storage = getStorage();
  const timestamp = new Date().getTime();
  const result: Record<string, string> = {};

  const uploadPromises = Object.entries(iconBlobs).map(async ([size, blob]) => {
    const filename = `${basePath}-${size}-${timestamp}.webp`;
    const storageRef = ref(storage, `branding/${filename}`);

    // Upload the blob
    const snapshot = await uploadBytes(storageRef, blob);

    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    result[size] = downloadURL;
  });

  await Promise.all(uploadPromises);

  return result;
}
